/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * TanStack Query hooks for admin branding (system settings).
 * Server state is managed by React Query; used by BrandingContext.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authenticatedApi } from "@/api/authenticatedClient";
import { BRANDING_QUERY_KEY } from "@/api/queryKeys";

/** API response shape (snake_case from backend). */
export interface BrandingApiResponse {
  app_name: string;
  app_tagline: string | null;
  app_description: string | null;
  help_title: string | null;
  help_intro: string | null;
  support_email: string | null;
  support_phone: string | null;
  state_code: string;
  state_name: string | null;
  agency_name: string | null;
  agency_abbreviation: string | null;
  logo_light: string | null;
  logo_dark: string | null;
  logo_icon: string | null;
  favicon: string | null;
  primary_color: string;
  primary_color_light: string;
  primary_color_dark: string;
  accent_color: string;
  footer_text: string | null;
  copyright_text: string | null;
  show_powered_by: boolean;
  /** Set when non-admin save created a change request. */
  change_request_id?: string | null;
  request_number?: string | null;
}

async function fetchBranding(): Promise<BrandingApiResponse> {
  const res = await authenticatedApi.get<BrandingApiResponse>("/admin/settings/branding");
  const data =
    typeof res === "object" && "app_name" in res
      ? res
      : ((res as { data?: BrandingApiResponse }).data ?? res);
  return data;
}

async function updateBrandingApi(body: Record<string, unknown>): Promise<BrandingApiResponse> {
  const res = await authenticatedApi.put<BrandingApiResponse>("/admin/settings/branding", body);
  const data =
    typeof res === "object" && "app_name" in res
      ? res
      : ((res as { data?: BrandingApiResponse }).data ?? res);
  return data;
}

export function useBrandingQuery() {
  return useQuery({
    queryKey: BRANDING_QUERY_KEY,
    queryFn: fetchBranding,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });
}

export function useBrandingMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateBrandingApi,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: BRANDING_QUERY_KEY });
    },
  });
}
